/* @jsxImportSource @emotion/react */

import React from 'react'
import { css } from '@emotion/react'
import classNames from 'classnames'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  BitvavoLogo,
  CanonLogo,
  FrontifyLogo,
  ImgSanity,
  LogitechLogo,
  LushLogo,
  Toggle,
  TooltipOnHover,
  VismaLogo,
} from '@slite/components'

import { Button } from '../../components/Button/Button'
import { Section } from '../../components/Section'
import { SourceIconsRow } from '../../components/SourceIconsRow'
import { WebsiteText } from '../../components/WebsiteText'
import { SMALL_BREAKPOINT } from '../../utils/breakpoints'
import type { PricingPlan } from './data'
import { getPricingPlans } from './data'
import { dottedUnderlineStyle } from './styles'

function PlanItem({
  plan,
  isYearly,
  setIsYearly,
}: {
  plan: PricingPlan
  isYearly: boolean
  setIsYearly: (isYearly: boolean) => void
}) {
  const intl = useIntl()
  const price = isYearly ? plan.price?.yearly : plan.price?.monthly
  return (
    <div
      className={classNames(
        'grid-span-4-large grid-span-6-medium grid-span-12-small',
        'overflow-hidden',
        'position-relative'
      )}
      css={css`
        margin-top: ${plan.accentuated ? '-32px' : '0'};
        /* On mobile the cards stack vertically, so the -32px lift on the
           accentuated card would push its 'Recommended' label over the card
           above it. Reset the lift on small screens. */
        @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
          margin-top: 0;
        }
      `}
      style={{
        alignSelf: 'start',
        backgroundColor: plan.accentuated
          ? `var(--saturn-orange)`
          : 'var(--shade-100)',
        padding: plan.accentuated ? '32px 2px 2px 2px' : 0,
        borderRadius: plan.accentuated ? 18 : 16,
        border: !plan.accentuated ? '1px solid rgba(0, 0, 0, 0.1)' : undefined,
      }}
    >
      {plan.accentuated && (
        <div
          className="position-absolute flexbox flexbox-centered"
          style={{ top: 0, left: 0, right: 0, height: 32 }}
        >
          <div className=" uppercase font-size-8 font-weight-bold white">
            <FormattedMessage
              id="pricing.recommended"
              defaultMessage="Recommended"
            />
          </div>
        </div>
      )}
      <div
        className={classNames(
          'position-relative overflow-hidden',
          'background-shade-100',
          'padding-horizontal-16 padding-top-24 padding-bottom-16',
          'text-align-left'
        )}
        style={{
          borderRadius: 16,
        }}
      >
        <div className="padding-8">
          <div style={{ minHeight: 90 }}>
            <WebsiteText variant="smallTitle">{plan.title}</WebsiteText>
            <p className="font-size-6 margin-top-8">{plan.description}</p>
          </div>

          <div className="margin-top-24 font-size-6">
            {plan.price === null ? (
              <WebsiteText variant="smallTitle" htmlTag="span">
                {intl.formatMessage({
                  id: 'pricing.enterprise.contactUs',
                  defaultMessage: "Let's talk",
                })}
              </WebsiteText>
            ) : (
              intl.formatMessage(
                {
                  id: 'pricing.pricepeuserrperiod',
                  defaultMessage: '{price} per user/month',
                },
                {
                  price: (
                    <WebsiteText
                      variant="smallTitle"
                      htmlTag="span"
                      // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                    >{`$${price}`}</WebsiteText>
                  ),
                }
              )
            )}

            <div className="flexbox flexbox-align-center font-size-7-not-small font-size-8 margin-top-8">
              {plan.price === null ? (
                intl.formatMessage({
                  id: 'pricing.enterprise.customPricing',
                  defaultMessage: 'Custom pricing',
                })
              ) : (
                <>
                  <Toggle
                    size="small"
                    checked={isYearly}
                    className="margin-right-8"
                    css={{
                      '&.react-toggle--checked .react-toggle-track': {
                        backgroundColor: 'var(--shade-00)',
                      },
                      '&.react-toggle--checked:hover .react-toggle-track, &.react-toggle--checked.react-toggle--focus .react-toggle-track':
                        {
                          backgroundColor: 'var(--shade-05)',
                        },
                    }}
                    onChange={(event) => {
                      setIsYearly(event.target.checked)
                    }}
                  />
                  {intl.formatMessage({
                    id: 'pricing.billedYearly',
                    defaultMessage: 'Billed yearly',
                  })}
                </>
              )}
            </div>
          </div>

          <Button
            href={plan.action.href}
            variant={plan.btnVariant}
            className="margin-top-32 width-full text-align-center"
            size="default"
          >
            {plan.action.label}
          </Button>

          <ul
            className="font-size-6 margin-top-16 line-height-copy flexbox flexbox-column"
            css={{ gap: 10 }}
          >
            {plan.features.map((feature, index) => (
              <li
                key={index}
                className="flexbox flexbox-column"
                style={feature.indent ? { paddingLeft: 20 } : undefined}
              >
                <div
                  className="flexbox flexbox-align-center"
                  style={{
                    ...(feature.comingSoon && !feature.red
                      ? { opacity: 0.4 }
                      : {}),
                    ...(feature.red ? { color: 'var(--danger-content)' } : {}),
                  }}
                >
                  <ImgSanity
                    className="margin-right-8"
                    alt="check"
                    src="https://cdn.sanity.io/images/dnsgqii5/production/91ab0da8fad96eef5a7b5913b51f0c7d52aee15f-121x121.webp"
                    width={12}
                    height={12}
                  />
                  {feature.tooltip ? (
                    <TooltipOnHover
                      positions={['top', 'left']}
                      allowContentHover
                      openOnTouch
                      containerStyle={{ pointerEvents: 'auto' }}
                      content={
                        <div
                          className="font-size-7 line-height-copy text-align-left shade-00"
                          style={{ maxWidth: 240 }}
                        >
                          {feature.tooltip}
                        </div>
                      }
                      target={({ ref, props }) => (
                        <span
                          ref={ref}
                          {...props}
                          style={{
                            ...dottedUnderlineStyle,
                            cursor: 'help',
                          }}
                        >
                          {feature.label}
                        </span>
                      )}
                    />
                  ) : (
                    feature.label
                  )}
                </div>
                {feature.showSourceIcons && <SourceIconsRow paddingLeft={20} />}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

export function SectionPricingPlans({
  isYearly,
  setIsYearly,
}: {
  isYearly: boolean
  setIsYearly: (isYearly: boolean) => void
}) {
  const intl = useIntl()
  const plans = getPricingPlans(intl)

  return (
    <Section
      background="moon-dust-lighter"
      className="text-align-center"
      containerBottomPadding={80}
    >
      <div className="grid-12" style={{ marginBottom: 80 }}>
        <div className="grid-span-8-large grid-offset-2-large grid-span-12 flexbox flexbox-column flexbox-align-center">
          <WebsiteText
            variant="display"
            htmlTag="h1"
            className="margin-bottom-16"
          >
            <FormattedMessage
              id="pricing.hero.title"
              defaultMessage="Plans for every stage of your company"
            />
          </WebsiteText>
        </div>
      </div>

      <div className="grid-12 grid-gap-16 margin-top-32">
        {plans.map((plan, index) => (
          <PlanItem
            key={index}
            plan={plan}
            isYearly={isYearly}
            setIsYearly={setIsYearly}
          />
        ))}
      </div>

      <div className="padding-top-60 font-size-3-not-medium font-size-4  text-align-center line-height-title font-weight-semibold">
        <FormattedMessage
          id="index.pricing.customers"
          defaultMessage="3,000+ leading companies use Slite as the memory layer for their AI-native teams."
        />
        <div
          className="margin-top-16"
          css={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            flexWrap: 'wrap',
          }}
        >
          {[
            { name: 'Lush', Logo: LushLogo },
            { name: 'Visma', Logo: VismaLogo },
            { name: 'Frontify', Logo: FrontifyLogo },
            { name: 'Canon', Logo: CanonLogo },
            { name: 'Logitech', Logo: LogitechLogo },
            { name: 'Bitvavo', Logo: BitvavoLogo },
          ].map(({ name, Logo }) => (
            <div
              key={name}
              css={{
                flex: '0 0 auto',
                padding: 12,
                minWidth: 90,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Logo
                css={{
                  height: 24,
                  display: 'inline-block',
                  flex: 'none',
                  objectFit: 'contain',
                  margin: 'auto',
                  maxWidth: 100,
                }}
                className="margin-horizontal-16 text-align-center margin-vertical-12"
              />
            </div>
          ))}
        </div>
      </div>
    </Section>
  )
}
